import { useEffect, useRef, useState } from 'react';
import { ANIMS_IN, ANIMS_OUT, ASPECTS, EFFECT_TYPES, FILTERS, MAX_SPEED, MIN_SPEED, MOTIONS, TEXT_DECOS, TEXT_FONTS, defaultLook, defaultProject, frameSize, makeText, type AnimIn, type AnimOut, type Aspect, type EditEffect, type EditFormat, type EditLook, type EditMotion, type EditOutput, type EditOverlay, type EditText, type EditTextStyle, type EditTransform, type EffectType, type FilterType, type KeyProp, type MotionType, type TextDeco, type TextFont } from '@airpost/shared';
import { lookPaint } from '../edit-look.js';
import { FrameRenderer, canvasFilters } from '../edit-render.js';
import { timecode } from '../edit-timeline.js';
import { t, type Key } from '../i18n.js';

/**
 * The editor's panels: a piece's colour, frame, speed and effects; a text's words, design, place and animation; a
 * picture over the sequence; the format and the export. Each is made of sections that open and close (remembered in
 * the browser), each value a slider with its number beside it; a value that can change over time has a keyframe
 * diamond (◇ none, ◇ amber: keyframes elsewhere, ◆ amber: one here). The page owns the project; a panel hands back
 * patches (with a key, so a run of slider moves is one undo) or goes through the page's keyframe kit.
 */
type Patch<T> = (patch: Partial<T>, key?: string) => void;

/** A keyframable value's state at the playhead, and how to change it. */
export type KeyState = 'none' | 'off' | 'on';
export interface KeyKit {
  value(prop: KeyProp): number;
  state(prop: KeyProp): KeyState;
  set(prop: KeyProp, v: number, key?: string): void;
  toggle(prop: KeyProp): void;
  /** The playhead to the item's previous or next keyframe. */
  jump(dir: -1 | 1): void;
  /** How many moments have keyframes. */
  count: number;
}

export const FILTER_WORDS: Record<FilterType, Key> = {
  none: 'veFlNone',
  bw: 'veFlBw',
  vivid: 'veFlVivid',
  warm: 'veFlWarm',
  cool: 'veFlCool',
  fade: 'veFlFade',
  cinematic: 'veFlCinematic',
  vintage: 'veFlVintage',
  noir: 'veFlNoir',
  dramatic: 'veFlDramatic',
};
export const EFFECT_WORDS: Record<EffectType, { key: Key; icon: string }> = {
  grain: { key: 'veFxGrain', icon: '░' },
  bars: { key: 'veFxBars', icon: '▬' },
  flash: { key: 'veFxFlash', icon: '✺' },
  shake: { key: 'veFxShake', icon: '≋' },
  glitch: { key: 'veFxGlitch', icon: '▚' },
};
export const MOTION_WORDS: Record<MotionType, { key: Key; icon: string }> = {
  none: { key: 'veMoNone', icon: '·' },
  'zoom-in': { key: 'veMoZoomIn', icon: '⊕' },
  'zoom-out': { key: 'veMoZoomOut', icon: '⊖' },
  'pan-left': { key: 'veMoPanLeft', icon: '←' },
  'pan-right': { key: 'veMoPanRight', icon: '→' },
  'pan-up': { key: 'veMoPanUp', icon: '↑' },
  'pan-down': { key: 'veMoPanDown', icon: '↓' },
};
const ANIM_IN_WORDS: Record<AnimIn, Key> = { none: 'veAnNone', fade: 'veAnFade', 'slide-up': 'veAnSlideUp', 'slide-left': 'veAnSlideLeft', pop: 'veAnPop', typewriter: 'veAnType', wipe: 'veAnWipe' };
const ANIM_OUT_WORDS: Record<AnimOut, Key> = { none: 'veAnNone', fade: 'veAnFade', 'slide-down': 'veAnSlideDown', 'slide-right': 'veAnSlideRight', pop: 'veAnPop', wipe: 'veAnWipe' };
const FONT_WORDS: Record<TextFont, Key> = { sans: 'veFontSans', serif: 'veFontSerif', condensed: 'veFontCondensed', display: 'veFontDisplay', mono: 'veFontMono' };
const DECO_WORDS: Record<TextDeco, Key> = { none: 'veDecoNone', bar: 'veDecoBar', underline: 'veDecoUnderline', tag: 'veDecoTag', frame: 'veDecoFrame' };
export const ASPECT_WORDS: Record<Aspect, Key> = { '16:9': 'veAspectWide', '9:16': 'veAspectTall', '1:1': 'veAspectSquare', '4:5': 'veAspectPortrait' };

/** A style the Text bin offers: a sample, a design, an animation. */
export interface TextPreset {
  id: string;
  name: Key;
  sample: Key;
  style: Partial<EditTextStyle>;
  animIn: AnimIn;
  animOut: AnimOut;
  animMs?: number;
}
/** Designed titles in the station's colours (the CG's blue and orange), animated in and out. */
export const TEXT_TEMPLATES: TextPreset[] = [
  { id: 'tpl-breaking', name: 'veTplBreaking', sample: 'veTplBreakingSample', style: { font: 'condensed', size: 4.6, uppercase: true, bg: '#ffffff', bgOpacity: 1, color: '#14181f', accent: '#c0392b', deco: 'tag', align: 'left', x: 0.38, y: 0.8, width: 0.64, shadow: false }, animIn: 'wipe', animOut: 'wipe', animMs: 500 },
  { id: 'tpl-name', name: 'veTplName', sample: 'veTplNameSample', style: { size: 4.4, bg: '#1f4a78', bgOpacity: 0.95, accent: '#ff9f1c', deco: 'bar', subScale: 0.7, align: 'left', x: 0.32, y: 0.82, width: 0.52, shadow: false }, animIn: 'wipe', animOut: 'wipe', animMs: 450 },
  { id: 'tpl-headline', name: 'veTplHeadline', sample: 'veTplHeadlineSample', style: { font: 'display', size: 7.5, uppercase: true, bg: '#000000', bgOpacity: 0.85, accent: '#ffe14d', deco: 'underline', x: 0.5, y: 0.2, width: 0.86, shadow: false }, animIn: 'pop', animOut: 'fade' },
  { id: 'tpl-quote', name: 'veTplQuote', sample: 'veTplQuoteSample', style: { font: 'serif', size: 5, bold: false, italic: true, bg: '#0f1114', bgOpacity: 0.82, accent: '#ff9f1c', deco: 'frame', subScale: 0.62, x: 0.5, y: 0.72, width: 0.78, shadow: false }, animIn: 'fade', animOut: 'fade' },
  { id: 'tpl-location', name: 'veTplLocation', sample: 'veTplLocationSample', style: { font: 'condensed', size: 3.4, uppercase: true, bg: '#14181f', bgOpacity: 0.85, accent: '#ff9f1c', deco: 'bar', align: 'left', x: 0.17, y: 0.1, width: 0.4, shadow: false }, animIn: 'wipe', animOut: 'wipe', animMs: 400 },
  { id: 'tpl-live', name: 'veTplLive', sample: 'veTplLiveSample', style: { font: 'condensed', size: 3.2, uppercase: true, bg: '#c0392b', bgOpacity: 1, x: 0.9, y: 0.08, width: 0.3, shadow: false }, animIn: 'pop', animOut: 'fade' },
  { id: 'tpl-figure', name: 'veTplFigure', sample: 'veTplFigureSample', style: { font: 'display', size: 12, color: '#ffe14d', subScale: 0.32, x: 0.5, y: 0.45, width: 0.7 }, animIn: 'pop', animOut: 'fade' },
  { id: 'tpl-section', name: 'veTplSection', sample: 'veTplSectionSample', style: { size: 7, uppercase: true, accent: '#ff9f1c', deco: 'underline', x: 0.5, y: 0.5, width: 0.8 }, animIn: 'wipe', animOut: 'fade', animMs: 500 },
];
/** Plain texts: a sample, a look. */
export const TEXT_PRESETS: TextPreset[] = [
  { id: 'title', name: 'veTxTitle', sample: 'veTxTitleSample', style: { size: 9, font: 'display', uppercase: true, y: 0.42 }, animIn: 'pop', animOut: 'fade' },
  { id: 'subtitle', name: 'veTxSubtitle', sample: 'veTxSubtitleSample', style: { size: 5, bold: false, y: 0.57 }, animIn: 'slide-up', animOut: 'fade' },
  { id: 'breaking', name: 'veTxBreaking', sample: 'veTxBreakingSample', style: { size: 5.5, font: 'condensed', uppercase: true, bg: '#c0392b', bgOpacity: 1, y: 0.12, width: 0.6, shadow: false }, animIn: 'slide-left', animOut: 'fade' },
  { id: 'quote', name: 'veTxQuote', sample: 'veTxQuoteSample', style: { size: 5, font: 'serif', bold: false, italic: true, bg: '#000000', bgOpacity: 0.55, y: 0.74, width: 0.76, shadow: false }, animIn: 'fade', animOut: 'fade' },
  { id: 'caption', name: 'veTxCaption', sample: 'veTxCaptionSample', style: { size: 4.2, bg: '#000000', bgOpacity: 0.6, y: 0.86, width: 0.86, shadow: false }, animIn: 'fade', animOut: 'fade' },
  { id: 'social', name: 'veTxSocial', sample: 'veTxSocialSample', style: { size: 7.5, font: 'display', color: '#ffe14d', outline: true, y: 0.3, width: 0.9 }, animIn: 'typewriter', animOut: 'pop' },
  { id: 'location', name: 'veTxLocation', sample: 'veTxLocationSample', style: { size: 3.6, font: 'condensed', uppercase: true, bg: '#1f4a78', bgOpacity: 0.92, x: 0.2, y: 0.1, align: 'left', width: 0.36, shadow: false }, animIn: 'slide-left', animOut: 'fade' },
  { id: 'credit', name: 'veTxCredit', sample: 'veTxCreditSample', style: { size: 2.8, bold: false, x: 0.84, y: 0.94, align: 'right', width: 0.3 }, animIn: 'fade', animOut: 'fade' },
];
export const presetById = (id: string): TextPreset => [...TEXT_TEMPLATES, ...TEXT_PRESETS].find((p) => p.id === id) ?? TEXT_PRESETS[0]!;

/** A template's look, drawn by the same renderer as the monitor and framed on the design, so its words can be read. */
export function TemplateThumb({ preset }: { preset: TextPreset }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    const x = makeText(0, t(preset.sample), { ...preset.style, rotation: 0 }, { animIn: 'none', animOut: 'none', durationMs: 4000 });
    const project = { ...defaultProject({ kind: 'clip', id: '-' }, 4000), texts: [x] };
    const off = document.createElement('canvas');
    off.width = 1280;
    off.height = 720;
    const octx = off.getContext('2d');
    if (!octx) return;
    const r = new FrameRenderer(off.width, off.height);
    r.render(octx, { project, tMs: 2000, spec: { bg: 'black', layers: [] }, imageFor: () => null, stillFor: () => null });
    const b = r.boxes.find((it) => it.kind === 'text');
    // the design with a margin, at the thumbnail's shape
    let w = b ? b.w * 1.3 + 40 : off.width;
    let h = b ? b.h * 1.3 + 40 : off.height;
    if (w / h > c.width / c.height) h = (w * c.height) / c.width;
    else w = (h * c.width) / c.height;
    const cx = b ? b.x + b.w / 2 : off.width / 2;
    const cy = b ? b.y + b.h / 2 : off.height / 2;
    ctx.fillStyle = '#16181c';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(off, cx - w / 2, cy - h / 2, w, h, 0, 0, c.width, c.height);
  }, [preset]);
  return <canvas ref={ref} width={320} height={180} className="ve-tpl-thumb" aria-hidden="true" />;
}

/** A CSS look for a plain style's card in the bin, close to how the canvas paints it. */
export function presetCss(style: Partial<EditTextStyle>): React.CSSProperties {
  const families: Record<TextFont, string> = { sans: 'system-ui, "Segoe UI", sans-serif', serif: 'Georgia, serif', condensed: '"Arial Narrow", "Segoe UI Semibold", sans-serif', display: '"Arial Black", Impact, sans-serif', mono: 'Consolas, monospace' };
  return {
    fontFamily: families[style.font ?? 'sans'],
    fontWeight: style.bold === false ? 400 : 700,
    fontStyle: style.italic ? 'italic' : 'normal',
    textTransform: style.uppercase ? 'uppercase' : 'none',
    color: style.color ?? '#ffffff',
    background: style.bg ? style.bg : 'transparent',
    WebkitTextStroke: style.outline ? '0.6px #000' : undefined,
    textShadow: style.shadow === false || style.bg ? 'none' : '0 1px 3px rgba(0,0,0,0.8)',
  };
}

// ── the building blocks ──

const SEC_KEY = 've-sec:';
const readOpen = (id: string, fallback: boolean): boolean => {
  try {
    const v = window.localStorage.getItem(SEC_KEY + id);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
};
/** A part of a panel that opens and closes; the browser remembers which. */
export function Section({ id, title, open: initial = true, extra, children }: { id: string; title: string; open?: boolean; extra?: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(() => readOpen(id, initial));
  const flip = () => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(SEC_KEY + id, next ? '1' : '0');
    } catch {
      /* the choice is not remembered */
    }
  };
  return (
    <div className={`ve-sec${open ? ' open' : ''}`}>
      <div className="ve-sec-head">
        <button type="button" className="ve-sec-toggle" onClick={flip} aria-expanded={open}>
          <span className="ve-sec-chev">{open ? '▾' : '▸'}</span>
          {title}
        </button>
        {extra}
      </div>
      {open && <div className="ve-sec-body">{children}</div>}
    </div>
  );
}

type Unit = 'pct' | 'signed' | 'deg' | 'num' | 'speed';
const shown = (v: number, unit: Unit): number => (unit === 'pct' || unit === 'signed' ? Math.round(v * 100) : unit === 'deg' ? Math.round(v) : unit === 'speed' ? Math.round(v * 100) / 100 : Math.round(v * 10) / 10);
const fromShown = (n: number, unit: Unit): number => (unit === 'pct' || unit === 'signed' ? n / 100 : n);
const suffix: Record<Unit, string> = { pct: '%', signed: '', deg: '°', num: '', speed: '×' };

/** The keyframe diamond of a value: ◇ none, ◇ (amber) keyframes elsewhere, ◆ one at the playhead. */
export function Diamond({ state, disabled, onToggle }: { state: KeyState; disabled: boolean; onToggle: () => void }) {
  return (
    <button type="button" className={`ve-kf ${state}`} disabled={disabled} onClick={onToggle} title={state === 'on' ? t('veKeyRemove') : t('veKeyAdd')} aria-pressed={state === 'on'}>
      {state === 'on' ? '◆' : '◇'}
    </button>
  );
}

/** A labelled slider with its value in a number box beside it, and the keyframe diamond of a value that has one. */
export function Slider({ label, value, min, max, step, unit, disabled, onChange, kf }: { label: string; value: number; min: number; max: number; step: number; unit: Unit; disabled: boolean; onChange: (v: number) => void; kf?: { state: KeyState; onToggle: () => void } }) {
  const clampV = (v: number) => Math.min(max, Math.max(min, v));
  return (
    <div className="ve-slider">
      <label>
        {kf ? <Diamond state={kf.state} disabled={disabled} onToggle={kf.onToggle} /> : <span className="ve-kf-space" />}
        {label}
      </label>
      <input type="range" min={min} max={max} step={step} value={clampV(value)} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="ve-num-wrap">
        <input
          type="number"
          className="ve-num"
          value={shown(value, unit)}
          step={unit === 'pct' || unit === 'signed' || unit === 'deg' ? 1 : unit === 'speed' ? 0.05 : 0.1}
          disabled={disabled}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (e.target.value !== '' && Number.isFinite(n)) onChange(clampV(fromShown(n, unit)));
          }}
        />
        <span className="ve-unit">{suffix[unit]}</span>
      </span>
    </div>
  );
}

/** The item's keyframes: to the previous or next, and how many. */
function KeyNav({ kit }: { kit: KeyKit }) {
  return (
    <div className="ve-keynav">
      <span className="ve-note">
        ◆ {kit.count} {t('veKeyframes')}
      </span>
      <span className="ve-grow" />
      <button type="button" className="btn small" disabled={!kit.count} onClick={() => kit.jump(-1)} title={t('veKeyPrev')}>
        ◂◆
      </button>
      <button type="button" className="btn small" disabled={!kit.count} onClick={() => kit.jump(1)} title={t('veKeyNext')}>
        ◆▸
      </button>
    </div>
  );
}

/** Place, size, turn and opacity of an item, each keyframable. */
function PlaceFields({ kit, size, canEdit, frame }: { kit: KeyKit; size: { prop: KeyProp; label: string; min: number; max: number; step: number; unit: Unit }; canEdit: boolean; frame: 'item' | 'piece' }) {
  const k = (prop: KeyProp) => ({ state: kit.state(prop), onToggle: () => kit.toggle(prop) });
  const xy = frame === 'piece' ? { min: -1, max: 1, unit: 'signed' as Unit } : { min: 0, max: 1, unit: 'pct' as Unit };
  return (
    <>
      <Slider label={t('veFrX')} value={kit.value('x')} min={xy.min} max={xy.max} step={0.005} unit={xy.unit} disabled={!canEdit} onChange={(v) => kit.set('x', v, 'x')} kf={k('x')} />
      <Slider label={t('veFrY')} value={kit.value('y')} min={xy.min} max={xy.max} step={0.005} unit={xy.unit} disabled={!canEdit} onChange={(v) => kit.set('y', v, 'y')} kf={k('y')} />
      <Slider label={size.label} value={kit.value(size.prop)} min={size.min} max={size.max} step={size.step} unit={size.unit} disabled={!canEdit} onChange={(v) => kit.set(size.prop, v, 'size')} kf={k(size.prop)} />
      <Slider label={t('veFrRotation')} value={kit.value('rotation')} min={-180} max={180} step={1} unit="deg" disabled={!canEdit} onChange={(v) => kit.set('rotation', v, 'rot')} kf={k('rotation')} />
      <Slider label={t('veFrOpacity')} value={kit.value('opacity')} min={0} max={1} step={0.01} unit="pct" disabled={!canEdit} onChange={(v) => kit.set('opacity', v, 'op')} kf={k('opacity')} />
      <KeyNav kit={kit} />
    </>
  );
}

// ── a piece ──

/** The piece's colour: a filter at a strength, then the adjustments. */
export function LookPanel({ look, thumb, canEdit, onChange }: { look: EditLook; thumb: string | null; canEdit: boolean; onChange: Patch<EditLook> }) {
  const plain = JSON.stringify(look) === JSON.stringify(defaultLook());
  return (
    <>
      <Section id="look-filter" title={t('veBinFilters')}>
        <div className="ve-filter-grid">
          {FILTERS.map((f) => (
            <button key={f} type="button" className={`ve-filter${look.filter === f ? ' sel' : ''}`} disabled={!canEdit} onClick={() => onChange({ filter: f, amount: f === look.filter ? look.amount : 1 })} title={t(FILTER_WORDS[f])}>
              <span className="ve-filter-swatch" style={{ backgroundImage: thumb ? `url(${thumb})` : undefined, filter: lookPaint({ ...defaultLook(), filter: f }).css === 'none' ? undefined : lookPaint({ ...defaultLook(), filter: f }).css }} />
              <span className="ve-filter-name">{t(FILTER_WORDS[f])}</span>
            </button>
          ))}
        </div>
        {look.filter !== 'none' && <Slider label={t('veLkAmount')} value={look.amount} min={0} max={1} step={0.05} unit="pct" disabled={!canEdit} onChange={(v) => onChange({ amount: v }, 'lk-amount')} />}
      </Section>
      <Section
        id="look-adjust"
        title={t('veLkAdjust')}
        extra={
          <button className="btn small" type="button" disabled={!canEdit || plain} onClick={() => onChange(defaultLook())}>
            ↺ {t('veReset')}
          </button>
        }
      >
        <Slider label={t('veLkBrightness')} value={look.brightness} min={-1} max={1} step={0.02} unit="signed" disabled={!canEdit} onChange={(v) => onChange({ brightness: v }, 'lk-b')} />
        <Slider label={t('veLkContrast')} value={look.contrast} min={-1} max={1} step={0.02} unit="signed" disabled={!canEdit} onChange={(v) => onChange({ contrast: v }, 'lk-c')} />
        <Slider label={t('veLkSaturation')} value={look.saturation} min={-1} max={1} step={0.02} unit="signed" disabled={!canEdit} onChange={(v) => onChange({ saturation: v }, 'lk-s')} />
        <Slider label={t('veLkTemperature')} value={look.temperature} min={-1} max={1} step={0.02} unit="signed" disabled={!canEdit} onChange={(v) => onChange({ temperature: v }, 'lk-t')} />
        <Slider label={t('veLkVignette')} value={look.vignette} min={0} max={1} step={0.02} unit="pct" disabled={!canEdit} onChange={(v) => onChange({ vignette: v }, 'lk-v')} />
        <Slider label={t('veLkBlur')} value={look.blur} min={0} max={1} step={0.02} unit="pct" disabled={!canEdit} onChange={(v) => onChange({ blur: v }, 'lk-bl')} />
        {!canvasFilters() && <p className="ve-note">{t('veNoCanvasFilters')}</p>}
      </Section>
    </>
  );
}

/** The piece's frame: place, size, turn and opacity (keyframable), mirror and crop; and its slow move. */
export function FramePanel({ transform: tr, motion, kit, canEdit, onTransform, onMotion, onReset }: { transform: EditTransform; motion: EditMotion; kit: KeyKit; canEdit: boolean; onTransform: Patch<EditTransform>; onMotion: Patch<EditMotion>; onReset: () => void }) {
  const crop = (side: keyof EditTransform['crop'], v: number) => onTransform({ crop: { ...tr.crop, [side]: v } }, `crop-${side}`);
  return (
    <>
      <Section
        id="frame-place"
        title={t('vePlaceSize')}
        extra={
          <button className="btn small" type="button" disabled={!canEdit} onClick={onReset}>
            ↺ {t('veReset')}
          </button>
        }
      >
        <PlaceFields kit={kit} canEdit={canEdit} frame="piece" size={{ prop: 'scale', label: t('veFrScale'), min: 0.1, max: 3, step: 0.01, unit: 'pct' }} />
        <div className="ve-field">
          <button type="button" className={`btn small${tr.flipH ? ' on' : ''}`} disabled={!canEdit} onClick={() => onTransform({ flipH: !tr.flipH })}>
            ⇋ {t('veFrFlipH')}
          </button>
          <button type="button" className={`btn small${tr.flipV ? ' on' : ''}`} disabled={!canEdit} onClick={() => onTransform({ flipV: !tr.flipV })}>
            ⇵ {t('veFrFlipV')}
          </button>
          <button type="button" className="btn small" disabled={!canEdit} onClick={() => kit.set('rotation', ((Math.round(kit.value('rotation') / 90) * 90 + 90 + 180) % 360) - 180)}>
            ↻ 90°
          </button>
        </div>
      </Section>
      <Section id="frame-crop" title={t('veFrCrop')} open={false}>
        <Slider label={t('veFrLeft')} value={tr.crop.left} min={0} max={0.45} step={0.01} unit="pct" disabled={!canEdit} onChange={(v) => crop('left', v)} />
        <Slider label={t('veFrRight')} value={tr.crop.right} min={0} max={0.45} step={0.01} unit="pct" disabled={!canEdit} onChange={(v) => crop('right', v)} />
        <Slider label={t('veFrTop')} value={tr.crop.top} min={0} max={0.45} step={0.01} unit="pct" disabled={!canEdit} onChange={(v) => crop('top', v)} />
        <Slider label={t('veFrBottom')} value={tr.crop.bottom} min={0} max={0.45} step={0.01} unit="pct" disabled={!canEdit} onChange={(v) => crop('bottom', v)} />
      </Section>
      <Section id="frame-motion" title={t('veMotion')} open={motion.type !== 'none'}>
        <div className="ve-chip-row">
          {MOTIONS.map((m) => (
            <button key={m} type="button" className={`ve-chip${motion.type === m ? ' sel' : ''}`} disabled={!canEdit} onClick={() => onMotion({ type: m })} title={t(MOTION_WORDS[m].key)}>
              {MOTION_WORDS[m].icon} {t(MOTION_WORDS[m].key)}
            </button>
          ))}
        </div>
        {motion.type !== 'none' && <Slider label={t('veMoAmount')} value={motion.amount} min={0.02} max={0.6} step={0.01} unit="pct" disabled={!canEdit} onChange={(v) => onMotion({ amount: v }, 'mo-amount')} />}
      </Section>
    </>
  );
}

const SPEEDS = [0.25, 0.5, 1, 1.5, 2, 4];
/** The piece's speed (its sound follows), and the frozen frame. */
export function SpeedPanel({ speed, still, freeze, lengthMs, canEdit, onSpeed, onFreeze }: { speed: number; still: boolean; freeze: boolean; lengthMs: number; canEdit: boolean; onSpeed: (v: number, key?: string) => void; onFreeze: () => void }) {
  return (
    <Section id="speed" title={t('veSpeed')}>
      {still ? (
        <p className="ve-note">{freeze ? t('veSpeedFrozen') : t('veSpeedPhoto')}</p>
      ) : (
        <>
          <Slider label={t('veSpeed')} value={speed} min={MIN_SPEED} max={MAX_SPEED} step={0.05} unit="speed" disabled={!canEdit} onChange={(v) => onSpeed(v, 'speed')} />
          <div className="ve-chip-row">
            {SPEEDS.map((s) => (
              <button key={s} type="button" className={`ve-chip${Math.abs(speed - s) < 0.001 ? ' sel' : ''}`} disabled={!canEdit} onClick={() => onSpeed(s)}>
                {s}×
              </button>
            ))}
          </div>
          <p className="ve-note">
            {t('veSpeedLength')} {timecode(lengthMs)} · {t('veSpeedHint')}
          </p>
        </>
      )}
      <div className="ve-field">
        <button type="button" className="btn small" disabled={!canEdit || still} onClick={onFreeze} title={t('veFreezeHint')}>
          ❄ {t('veFreeze')}
        </button>
      </div>
    </Section>
  );
}

/** The piece's effects, each on or off with its strength. */
export function EffectsPanel({ effects, canEdit, onChange }: { effects: EditEffect[]; canEdit: boolean; onChange: (effects: EditEffect[], key?: string) => void }) {
  return (
    <Section id="fx" title={t('veBinEffects')}>
      {EFFECT_TYPES.map((type) => {
        const on = effects.find((e) => e.type === type);
        return (
          <div key={type} className="ve-fx-row">
            <label className="ve-check-line">
              <input type="checkbox" checked={!!on} disabled={!canEdit} onChange={(e) => onChange(e.target.checked ? [...effects, { type, amount: 0.5 }] : effects.filter((x) => x.type !== type))} /> {EFFECT_WORDS[type].icon} {t(EFFECT_WORDS[type].key)}
            </label>
            {on && <input type="range" min={0} max={1} step={0.05} value={on.amount} disabled={!canEdit} onChange={(e) => onChange(effects.map((x) => (x.type === type ? { ...x, amount: Number(e.target.value) } : x)), `fx-${type}`)} />}
          </div>
        );
      })}
      <p className="ve-note">{t('veFxHint')}</p>
    </Section>
  );
}

// ── texts and pictures ──

const SPOTS: Array<{ x: number; y: number; icon: string }> = [
  { x: 0.2, y: 0.12, icon: '↖' },
  { x: 0.5, y: 0.12, icon: '↑' },
  { x: 0.8, y: 0.12, icon: '↗' },
  { x: 0.2, y: 0.5, icon: '←' },
  { x: 0.5, y: 0.5, icon: '•' },
  { x: 0.8, y: 0.5, icon: '→' },
  { x: 0.2, y: 0.86, icon: '↙' },
  { x: 0.5, y: 0.86, icon: '↓' },
  { x: 0.8, y: 0.86, icon: '↘' },
];
function Spots({ kit, canEdit }: { kit: KeyKit; canEdit: boolean }) {
  const x = kit.value('x');
  const y = kit.value('y');
  return (
    <div className="ve-spots" title={t('veSpotsHint')}>
      {SPOTS.map((s) => (
        <button
          key={s.icon}
          type="button"
          className={Math.abs(s.x - x) < 0.06 && Math.abs(s.y - y) < 0.06 ? 'sel' : ''}
          disabled={!canEdit}
          onClick={() => {
            kit.set('x', s.x);
            kit.set('y', s.y);
          }}
        >
          {s.icon}
        </button>
      ))}
    </div>
  );
}

export function AnimFields({ animIn, animOut, animMs, canEdit, typewriter, onChange }: { animIn: AnimIn; animOut: AnimOut; animMs: number; canEdit: boolean; typewriter: boolean; onChange: (patch: { animIn?: AnimIn; animOut?: AnimOut; animMs?: number }, key?: string) => void }) {
  return (
    <>
      <div className="ve-field">
        <label>{t('veAnIn')}</label>
        <select value={animIn} disabled={!canEdit} onChange={(e) => onChange({ animIn: e.target.value as AnimIn })}>
          {ANIMS_IN.filter((a) => typewriter || a !== 'typewriter').map((a) => (
            <option key={a} value={a}>
              {t(ANIM_IN_WORDS[a])}
            </option>
          ))}
        </select>
      </div>
      <div className="ve-field">
        <label>{t('veAnOut')}</label>
        <select value={animOut} disabled={!canEdit} onChange={(e) => onChange({ animOut: e.target.value as AnimOut })}>
          {ANIMS_OUT.map((a) => (
            <option key={a} value={a}>
              {t(ANIM_OUT_WORDS[a])}
            </option>
          ))}
        </select>
      </div>
      <Slider label={t('veAnLength')} value={animMs / 1000} min={0} max={2} step={0.05} unit="num" disabled={!canEdit} onChange={(v) => onChange({ animMs: Math.round(v * 1000) }, 'an-ms')} />
    </>
  );
}

const s1 = (ms: number) => (Math.round(ms / 100) / 10).toFixed(1);
function TimeFields({ atMs, durationMs, canEdit, nowMs, onChange }: { atMs: number; durationMs: number; canEdit: boolean; nowMs: number; onChange: (patch: { atMs?: number; durationMs?: number }, key?: string) => void }) {
  return (
    <>
      <div className="ve-field">
        <label>{t('veStrapAt')}</label>
        <input type="number" min={0} step={0.1} value={s1(atMs)} disabled={!canEdit} onChange={(e) => onChange({ atMs: Math.round(Number(e.target.value) * 1000) }, 'at')} />
        <span className="ve-unit">″</span>
        <button className="btn small" type="button" disabled={!canEdit} onClick={() => onChange({ atMs: Math.round(nowMs) })} title={t('veStrapHere')}>
          ⌖
        </button>
      </div>
      <div className="ve-field">
        <label>{t('veStrapDur')}</label>
        <input type="number" min={0.2} step={0.1} value={s1(durationMs)} disabled={!canEdit} onChange={(e) => onChange({ durationMs: Math.round(Number(e.target.value) * 1000) }, 'dur')} />
        <span className="ve-unit">″</span>
      </div>
    </>
  );
}

/** A text: its words, design, place, animation and time. */
export function TextPanel({ text: x, kit, canEdit, nowMs, onChange, onRemove, onStyleAll }: { text: EditText; kit: KeyKit; canEdit: boolean; nowMs: number; onChange: (patch: Partial<EditText>, key?: string) => void; onRemove: () => void; onStyleAll: (() => void) | null }) {
  const st = x.style;
  const style = (patch: Partial<EditTextStyle>, key?: string) => onChange({ style: { ...st, ...patch } }, key ? `st-${key}:${x.id}` : undefined);
  return (
    <>
      <Section id="tx-words" title={t('veText')}>
        <textarea className="ve-words" rows={3} value={x.text} maxLength={500} placeholder={t('veTxPlaceholder')} disabled={!canEdit} onChange={(e) => onChange({ text: e.target.value }, `words:${x.id}`)} />
        <p className="ve-note">{t('veTxLinesHint')}</p>
      </Section>
      <Section id="tx-style" title={t('veTxDesign')}>
        <div className="ve-field">
          <label>{t('veTxFont')}</label>
          <select value={st.font} disabled={!canEdit} onChange={(e) => style({ font: e.target.value as TextFont })}>
            {TEXT_FONTS.map((f) => (
              <option key={f} value={f}>
                {t(FONT_WORDS[f])}
              </option>
            ))}
          </select>
          <span className="ve-toggles">
            <button type="button" className={st.bold ? 'on' : ''} disabled={!canEdit} onClick={() => style({ bold: !st.bold })} title={t('veTxBold')}>
              <b>B</b>
            </button>
            <button type="button" className={st.italic ? 'on' : ''} disabled={!canEdit} onClick={() => style({ italic: !st.italic })} title={t('veTxItalic')}>
              <i>I</i>
            </button>
            <button type="button" className={st.uppercase ? 'on' : ''} disabled={!canEdit} onClick={() => style({ uppercase: !st.uppercase })} title={t('veTxUpper')}>
              ΑΑ
            </button>
          </span>
        </div>
        <div className="ve-field">
          <label>{t('veTxColor')}</label>
          <input type="color" value={st.color} disabled={!canEdit} onChange={(e) => style({ color: e.target.value }, 'color')} />
          <label className="ve-check-line">
            <input type="checkbox" checked={!!st.bg} disabled={!canEdit} onChange={(e) => style({ bg: e.target.checked ? '#000000' : null })} /> {t('veTxBox')}
          </label>
          {st.bg && <input type="color" value={st.bg} disabled={!canEdit} onChange={(e) => style({ bg: e.target.value }, 'bg')} />}
        </div>
        {st.bg && <Slider label={t('veTxBoxOpacity')} value={st.bgOpacity} min={0} max={1} step={0.05} unit="pct" disabled={!canEdit} onChange={(v) => style({ bgOpacity: v }, 'bgop')} />}
        <div className="ve-field">
          <label>{t('veTxDeco')}</label>
          <select value={st.accent ? st.deco : 'none'} disabled={!canEdit} onChange={(e) => style({ deco: e.target.value as TextDeco, accent: st.accent ?? '#ff9f1c' })}>
            {TEXT_DECOS.map((d) => (
              <option key={d} value={d}>
                {t(DECO_WORDS[d])}
              </option>
            ))}
          </select>
          {st.deco !== 'none' && <input type="color" value={st.accent ?? '#ff9f1c'} disabled={!canEdit} onChange={(e) => style({ accent: e.target.value }, 'accent')} title={t('veTxAccent')} />}
        </div>
        <Slider label={t('veTxSecond')} value={st.subScale} min={0.3} max={1} step={0.01} unit="pct" disabled={!canEdit} onChange={(v) => style({ subScale: v }, 'sub')} />
        <div className="ve-field">
          <label className="ve-check-line">
            <input type="checkbox" checked={st.outline} disabled={!canEdit} onChange={(e) => style({ outline: e.target.checked })} /> {t('veTxOutline')}
          </label>
          <label className="ve-check-line">
            <input type="checkbox" checked={st.shadow} disabled={!canEdit} onChange={(e) => style({ shadow: e.target.checked })} /> {t('veTxShadow')}
          </label>
          <span className="ve-toggles">
            {(['left', 'center', 'right'] as const).map((a) => (
              <button key={a} type="button" className={st.align === a ? 'on' : ''} disabled={!canEdit} onClick={() => style({ align: a })} title={t(a === 'left' ? 'veTxLeft' : a === 'right' ? 'veTxRight' : 'veTxCenter')}>
                {a === 'left' ? '⇤' : a === 'right' ? '⇥' : '↔'}
              </button>
            ))}
          </span>
        </div>
        {onStyleAll && (
          <button type="button" className="btn small" disabled={!canEdit} onClick={onStyleAll} title={t('veCaptionStyleAllHint')}>
            ⧉ {t('veCaptionStyleAll')}
          </button>
        )}
      </Section>
      <Section id="tx-place" title={t('vePlaceSize')}>
        <div className="ve-place">
          <Spots kit={kit} canEdit={canEdit} />
          <p className="ve-note">{t('veDragOnMonitor')}</p>
        </div>
        <PlaceFields kit={kit} canEdit={canEdit} frame="item" size={{ prop: 'size', label: t('veTxSize'), min: 1, max: 30, step: 0.1, unit: 'num' }} />
        <Slider label={t('veTxWidth')} value={st.width} min={0.1} max={1} step={0.01} unit="pct" disabled={!canEdit} onChange={(v) => style({ width: v }, 'w')} />
      </Section>
      <Section id="tx-anim" title={t('veAnimation')}>
        <AnimFields animIn={x.animIn} animOut={x.animOut} animMs={x.animMs} canEdit={canEdit} typewriter onChange={(patch, key) => onChange(patch, key ? `${key}:${x.id}` : undefined)} />
      </Section>
      <Section id="tx-time" title={t('veTime')}>
        <TimeFields atMs={x.atMs} durationMs={x.durationMs} canEdit={canEdit} nowMs={nowMs} onChange={(patch, key) => onChange(patch, key ? `${key}:${x.id}` : undefined)} />
      </Section>
      <div className="ve-field">
        <span className="ve-grow" />
        <button className="btn small danger" type="button" disabled={!canEdit} onClick={onRemove} title={t('delete')}>
          🗑 {t('veRemovePiece')}
        </button>
      </div>
    </>
  );
}

/** A picture over the sequence: where, how big, how see-through, its turn (all keyframable), its animation and time. */
export function OverlayPanel({ overlay: o, src, kit, canEdit, nowMs, onChange, onRemove }: { overlay: EditOverlay; src: string; kit: KeyKit; canEdit: boolean; nowMs: number; onChange: (patch: Partial<EditOverlay>, key?: string) => void; onRemove: () => void }) {
  return (
    <>
      <Section id="ov-place" title={t('vePlaceSize')}>
        <div className="ve-place">
          <img className="ve-overlay-thumb" src={src} alt="" />
          <Spots kit={kit} canEdit={canEdit} />
        </div>
        <PlaceFields kit={kit} canEdit={canEdit} frame="item" size={{ prop: 'width', label: t('veOvWidth'), min: 0.02, max: 1, step: 0.01, unit: 'pct' }} />
        <p className="ve-note">{t('veDragOnMonitor')}</p>
      </Section>
      <Section id="ov-anim" title={t('veAnimation')}>
        <AnimFields animIn={o.animIn} animOut={o.animOut} animMs={o.animMs} canEdit={canEdit} typewriter={false} onChange={(patch, key) => onChange(patch, key ? `${key}:${o.id}` : undefined)} />
      </Section>
      <Section id="ov-time" title={t('veTime')}>
        <TimeFields atMs={o.atMs} durationMs={o.durationMs} canEdit={canEdit} nowMs={nowMs} onChange={(patch, key) => onChange(patch, key ? `${key}:${o.id}` : undefined)} />
      </Section>
      <div className="ve-field">
        <span className="ve-grow" />
        <button className="btn small danger" type="button" disabled={!canEdit} onClick={onRemove} title={t('delete')}>
          🗑 {t('veRemovePiece')}
        </button>
      </div>
    </>
  );
}

/** The picture's shape and fill, and the exported file's size and frame rate. */
export function FormatPanel({ format, output, canEdit, onFormat, onOutput }: { format: EditFormat; output: EditOutput; canEdit: boolean; onFormat: Patch<EditFormat>; onOutput: Patch<EditOutput> }) {
  const size = frameSize(format, output.resolution);
  return (
    <>
      <Section id="fmt-shape" title={t('veShape')}>
        <div className="ve-aspect-grid">
          {ASPECTS.map((a) => {
            const [aw, ah] = a.split(':').map(Number) as [number, number];
            return (
              <button key={a} type="button" className={`ve-aspect${format.aspect === a ? ' sel' : ''}`} disabled={!canEdit} onClick={() => onFormat({ aspect: a })}>
                <span className="ve-aspect-box" style={{ aspectRatio: `${aw} / ${ah}` }} />
                <b>{a}</b>
                <span>{t(ASPECT_WORDS[a])}</span>
              </button>
            );
          })}
        </div>
      </Section>
      <Section id="fmt-fill" title={t('veBackground')}>
        <div className="ve-field">
          <label>{t('veFit')}</label>
          <select value={format.fit} disabled={!canEdit} onChange={(e) => onFormat({ fit: e.target.value as EditFormat['fit'] })}>
            <option value="contain">{t('veFitContain')}</option>
            <option value="cover">{t('veFitCover')}</option>
          </select>
        </div>
        <div className="ve-field">
          <label>{t('veBackground')}</label>
          <select value={format.background} disabled={!canEdit} onChange={(e) => onFormat({ background: e.target.value as EditFormat['background'] })}>
            <option value="black">{t('veBgBlack')}</option>
            <option value="blur">{t('veBgBlur')}</option>
            <option value="color">{t('veBgColor')}</option>
          </select>
          {format.background === 'color' && <input type="color" value={format.color} disabled={!canEdit} onChange={(e) => onFormat({ color: e.target.value }, 'bgcolor')} />}
        </div>
      </Section>
      <Section id="fmt-out" title={t('veOutput')}>
        <div className="ve-field">
          <label>{t('veResolution')}</label>
          <select value={output.resolution} disabled={!canEdit} onChange={(e) => onOutput({ resolution: Number(e.target.value) === 1080 ? 1080 : 720 })}>
            <option value={720}>720p</option>
            <option value={1080}>1080p</option>
          </select>
        </div>
        <div className="ve-field">
          <label>{t('veFps')}</label>
          <select value={output.fps} disabled={!canEdit} onChange={(e) => onOutput({ fps: Number(e.target.value) === 50 ? 50 : Number(e.target.value) === 30 ? 30 : 25 })}>
            <option value={25}>25 fps</option>
            <option value={30}>30 fps</option>
            <option value={50}>50 fps</option>
          </select>
        </div>
        <p className="ve-note">
          {size.w}×{size.h} · {output.fps} fps · {t('veFormatNote')}
        </p>
      </Section>
    </>
  );
}
